import { useState, type MouseEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import type { ShoppingCartDTO } from '@t/api.types';
import type { CategoryType, Plan } from '@t/productOffering.types';
import { getCategoryFromItemName } from '@utils/cartUtils';
import { isCouponItem } from '@hooks/useShoppingCart/coupon';
import { findMatchingPlan } from '@hooks/useShoppingCartDrawer/useShoppingCartDrawer';
import { useDeleteCartItem } from '@hooks/useDeleteCartItem/useDeleteCartItem';
import { PlanDetailsModal } from '@components/ProductOffers/modals/PlanDetailsModal';
import { useProductOfferings } from '@hooks/useProductOfferings/useProductOfferings';
import { useCartEquipment } from '@hooks/useCartEquipment/useCartEquipment';
import { useSessionContext } from '@contexts/SessionContext';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { CartItemCard } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard';
import { WarningDialog } from '@shared/components/WarningDialog/WarningDialog';
import { useTenantConfigContext } from '@providers/TenantConfigProvider';
import { getTermsPdfUrl } from '@services/termsPdfService';
import { TermsAndConditionsDialog } from './TermsAndConditionsDialog';

interface ProductReviewStepProps {
  isActive: boolean;
  cart: ShoppingCartDTO | null;
  termsAccepted: boolean;
  isCartMutating?: boolean;
  onTermsChange: (accepted: boolean) => void;
  onRemoveItem: (offeringId: string) => void;
  onRemoveAllItems: () => void;
}

export const ProductReviewStep = ({
  isActive,
  cart,
  termsAccepted,
  isCartMutating,
  onTermsChange,
  onRemoveItem,
  onRemoveAllItems,
}: ProductReviewStepProps) => {
  const theme = useTheme();

  const { sessionId } = useSessionContext();
  const { resolvedAddressId, qualifiedAddressId } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      qualifiedAddressId: selectQualifiedAddressId(s),
    }))
  );
  const analytics = useAnalytics();
  const { plansByCategory } = useProductOfferings(sessionId ?? '', resolvedAddressId ?? '');
  const equipmentByPlanId = useCartEquipment(sessionId ?? '', resolvedAddressId ?? '', cart);

  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  // TMF tenants are shown the SimplyAsk.ai terms instead of the Novus document. The document depends on the
  // tenant config, so the agreement stays disabled until it has loaded rather than opening the wrong PDF.
  const { tmfEnabled, isLoading: isTenantConfigLoading } = useTenantConfigContext();
  const [detailsPlan, setDetailsPlan] = useState<Plan | null>(null);

  const { handleDeleteItem, deleteInternetDialogOpen, closeDeleteInternetDialog, confirmDeleteInternet } =
    useDeleteCartItem({ cart, onRemoveItem, onRemoveAllItems });

  if (!isActive) return null;

  const items = (cart?.cartItem ?? []).filter((item) => !isCouponItem(item));
  const sortOrder: Record<string, number> = { Internet: 0, Video: 1, Phone: 2 };
  const sortedItems = [...items].sort((a, b) => {
    const catA = getCategoryFromItemName(a.productOffering?.name);
    const catB = getCategoryFromItemName(b.productOffering?.name);
    return (sortOrder[catA ?? ''] ?? 9) - (sortOrder[catB ?? ''] ?? 9);
  });

  const handleTermsClick = () => {
    if (termsAccepted) {
      onTermsChange(false);
    } else {
      setTermsDialogOpen(true);
    }
  };

  const handleAcceptTerms = () => {
    onTermsChange(true);
    setTermsDialogOpen(false);
    if (sessionId && cart) {
      analytics.fire('trackTermsAgreed', {
        sessionId,
        cart,
        qualification: { qualifiedAddressId, resolvedAddressId },
      });
    }
  };

  const handleDeclineTerms = () => {
    onTermsChange(false);
    setTermsDialogOpen(false);
  };

  const closeTermsDialog = () => {
    setTermsDialogOpen(false);
  };

  return (
    <>
      <Typography variant="body.md" color={theme.colors.primary}>
        Review the products in your cart before placing the order.
      </Typography>

      <Stack gap="16px" mb="24px" mt="16px">
        {sortedItems.map((item, i) => {
          const plan = findMatchingPlan(item, plansByCategory);
          return (
            <CartItemCard
              key={item.id ?? i}
              item={item}
              plan={plan}
              equipmentList={equipmentByPlanId.get(item.productOffering?.id ?? '') ?? []}
              onViewDetails={() => {
                if (plan) setDetailsPlan(plan);
              }}
              onDelete={() => handleDeleteItem(item)}
            />
          );
        })}
      </Stack>

      <Divider sx={{ my: '16px' }} />

      <Typography variant="label.button" color={theme.colors.primary}>
        Agreements &amp; Consents
      </Typography>
      <Stack direction="row" alignItems="center" gap="8px" mb="16px" mt="8px">
        <FormControlLabel
          sx={{ gap: '4px' }}
          control={
            <Checkbox checked={termsAccepted} onChange={handleTermsClick} disabled={isTenantConfigLoading} />
          }
          label={
            <Typography variant="body.md">
              I have read and agree to the{' '}
              <Typography
                variant="link.action"
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isTenantConfigLoading) setTermsDialogOpen(true);
                }}
              >
                Terms and Conditions
              </Typography>
            </Typography>
          }
        />
      </Stack>

      <TermsAndConditionsDialog
        open={termsDialogOpen}
        onClose={closeTermsDialog}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
        pdfUrl={getTermsPdfUrl(tmfEnabled)}
      />

      <PlanDetailsModal
        open={!!detailsPlan}
        onClose={() => setDetailsPlan(null)}
        plan={detailsPlan}
        categoryType={(getCategoryFromItemName(detailsPlan?.name) || 'Internet') as CategoryType}
      />

      <WarningDialog
        open={deleteInternetDialogOpen}
        onClose={closeDeleteInternetDialog}
        title="Internet Connection Required"
        description="To provide TV or Phone services, a Novus internet plan must remain in your cart. Removing internet will also remove all other services from your current order."
        outlinedButtonText="KEEP MY PLAN"
        containedButtonText="EMPTY CART"
        loading={isCartMutating}
        onOutlinedClick={closeDeleteInternetDialog}
        onContainedClick={confirmDeleteInternet}
      />
    </>
  );
};
